import React, { useState, useEffect, useCallback } from "react";
import { Formik } from "formik";
import * as Yup from "yup";

import { openFileUrl } from "../services/fileUtils";

import {
  Paper,
  Typography,
  Tabs,
  Tab,
  Badge,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";

import {
  UploadFile,
  Description,
  Visibility,
  Delete,
  Done,
  Archive,
} from "@mui/icons-material";

import auth from "../services/auth";
import {
  deleteService,
  getServices,
  getUnreadFileCounts,
  markFileAsRead,
  moveClientFileToClientArchive,
  uploadService,
} from "../services/source";

/* =========================
    VALIDATION
  ========================= */
const getServiceSchema = (tabIndex) => {
  return Yup.object({
    data_inicio: Yup.string(),
    observacoes: Yup.string(),
  });
};

/* =========================
    SERVICE CATEGORIES
  ========================= */
const serviceCategories = [
  "Contabilidade",
  "Fiscalidade",
  "RH",
  "Administrativo",
  "Formação",
  "Consultoria",
];

const accountingSubcategories = [
  "Compras",
  "Vendas",
  "Operações Diversas",
  "Bancos",
  "Caixa",
];

const administrativoSubcategories = [
  "AGT",
  "Bancos",
  "Jurídicos",
  "Comunicados",
];

const formacaoSubcategories = ["AGT", "Bancos", "Jurídicos"];

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isLockedComunicadosFromInternalArchive = (file) => {
  const tab = normalize(file?.tab_nome);
  const categoria = normalize(file?.categoria);
  const nomeFicheiro = normalize(file?.nome_ficheiro);

  const isConsultoria =
    tab === "administrativo" || categoria === "administrativo - comunicados";
  const isFromInternalArchive = nomeFicheiro.startsWith("ia_");

  return isConsultoria && isFromInternalArchive;
};

const Servicos = ({ clientId, companyId: companyIdProp }) => {
  const currentUser = auth.getCurrentUser();
  const userId = currentUser?.data?.id;
  const userType = currentUser?.data?.type;
  const companyId =
    companyIdProp ||
    currentUser?.data?.companyId ||
    currentUser?.data?.id ||
    "";
  const [selectedServiceTab, setSelectedServiceTab] = useState(0);
  const [selectedAccountingSubTab, setSelectedAccountingSubTab] = useState(0);
  const [selectedAdministrativoSubTab, setSelectedAdministrativoSubTab] =
    useState(0);
  const [selectedFormacaoSubTab, setSelectedFormacaoSubTab] = useState(0);
  const [services, setServices] = useState({});
  const [dbServices, setDbServices] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [unreadByCategory, setUnreadByCategory] = useState({});
  const [movingFileId, setMovingFileId] = useState(0);
  const [openingFileId, setOpeningFileId] = useState(0);
  const [archiveError, setArchiveError] = useState("");
  const [archiveSuccess, setArchiveSuccess] = useState("");
  const FILE_BASE_URL = process.env.REACT_APP_API_URL;

  const refreshUnreadCount = useCallback(async () => {
    if (!clientId || !userId || !userType) {
      setUnreadByCategory({});
      return;
    }

    const res = await getUnreadFileCounts({
      user_id: userId,
      user_type: userType,
      client_id: clientId,
    });

    if (res?.success) {
      setUnreadByCategory(res?.data?.servicos_por_categoria || {});
    }
  }, [clientId, userId, userType]);

  const getTabUnreadCount = useCallback(
    (tabLabel) => {
      const target = normalize(tabLabel);

      return Object.entries(unreadByCategory).reduce(
        (total, [category, count]) => {
          const normalizedCategory = normalize(category);
          const belongsToTab =
            normalizedCategory === target ||
            normalizedCategory.startsWith(`${target} - `);

          return belongsToTab ? total + Number(count || 0) : total;
        },
        0,
      );
    },
    [unreadByCategory],
  );

  const getSubtabUnreadCount = useCallback(
    (tabLabel, subtabLabel) => {
      const target = normalize(`${tabLabel} - ${subtabLabel}`);

      return Object.entries(unreadByCategory).reduce(
        (total, [category, count]) =>
          normalize(category) === target ? total + Number(count || 0) : total,
        0,
      );
    },
    [unreadByCategory],
  );

  const loadServices = useCallback(async () => {
    if (!clientId) return;

    const res = await getServices(clientId);

    if (res?.success) {
      setDbServices(res.data);
    }
  }, [clientId]);

  const getCurrentTabAndSubTab = () => {
    const tab = serviceCategories[selectedServiceTab] || "";

    if (selectedServiceTab === 0) {
      return {
        tab,
        subtab: accountingSubcategories[selectedAccountingSubTab] || "",
      };
    }

    if (selectedServiceTab === 3) {
      return {
        tab,
        subtab: administrativoSubcategories[selectedAdministrativoSubTab] || "",
      };
    }

    if (selectedServiceTab === 4) {
      return {
        tab,
        subtab: formacaoSubcategories[selectedFormacaoSubTab] || "",
      };
    }

    return { tab, subtab: "" };
  };

  /* =========================
     GET CATEGORY FOR UPLOAD
  ========================= */
  const getServiceCategory = () => {
    const { tab, subtab } = getCurrentTabAndSubTab();
    return subtab ? `${tab} - ${subtab}` : tab;
  };

  /* =========================
     LOAD DB SERVICES UseEffect
  ========================= */
  useEffect(() => {
    loadServices();
  }, [loadServices]);

  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  /* =========================
     LOCAL UPLOAD
  ========================= */
  const handleServiceUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const file = files[0];

    const fileEntry = {
      file,
      name: file.name,
      size: file.size,
      data_inicio: "",
      observacoes: "",
      uploaded: false,
    };

    const key =
      selectedServiceTab === 0
        ? `${selectedServiceTab}-${selectedAccountingSubTab}`
        : selectedServiceTab === 3
          ? `${selectedServiceTab}-${selectedAdministrativoSubTab}`
          : selectedServiceTab === 4
            ? `${selectedServiceTab}-${selectedFormacaoSubTab}`
            : selectedServiceTab;

    setServices((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), fileEntry],
    }));
    event.target.value = "";
  };

  /* =========================
     UPLOAD
  ========================= */
  const uploadFileToServer = async (fileEntry) => {
    const formData = new FormData();
    const { tab, subtab } = getCurrentTabAndSubTab();

    formData.append("file", fileEntry.file);
    formData.append("cliente_id", clientId);
    formData.append("company_id", companyId);
    formData.append("user_id", userId || "");
    formData.append("user_type", userType || "");
    formData.append("tab", tab || "");
    formData.append("subtab", subtab || "");
    formData.append("categoria", getServiceCategory());
    formData.append("data_inicio", fileEntry.data_inicio || "");
    formData.append("observacoes", fileEntry.observacoes || "");

    const res = await uploadService(formData);
    return res?.data?.success ? Number(res?.data?.id || 0) : 0;
  };

  /* =========================
     REMOVE LOCAL ONLY
  ========================= */
  const removeService = (index) => {
    const key =
      selectedServiceTab === 0
        ? `${selectedServiceTab}-${selectedAccountingSubTab}`
        : selectedServiceTab === 3
          ? `${selectedServiceTab}-${selectedAdministrativoSubTab}`
          : selectedServiceTab === 4
            ? `${selectedServiceTab}-${selectedFormacaoSubTab}`
            : selectedServiceTab;
    setServices((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== index),
    }));
  };

  /* =========================
     FILTER DB BY TAB
  ========================= */
  const filteredDbServices = dbServices.filter((svc) => {
    const { tab, subtab } = getCurrentTabAndSubTab();
    const expectedCategory = getServiceCategory();

    const byTabSubTab =
      (svc.tab_nome || "") === tab &&
      (svc.subtab_nome || "") === (subtab || "");

    const byCategory =
      (svc.categoria || "").trim() === (expectedCategory || "").trim();

    return byTabSubTab || byCategory;
  });

  const localService =
    selectedServiceTab === 0
      ? services[`${selectedServiceTab}-${selectedAccountingSubTab}`] || []
      : selectedServiceTab === 3
        ? services[`${selectedServiceTab}-${selectedAdministrativoSubTab}`] ||
          []
        : selectedServiceTab === 4
          ? services[`${selectedServiceTab}-${selectedFormacaoSubTab}`] || []
          : services[selectedServiceTab] || [];

  /* =========================
    DELETE SERVICE
  ========================= */
  const handleDeleteService = (file) => {
    setSelectedFile(file);
    setDeleteModalOpen(true);
  };

  const confirmDeleteService = async () => {
    if (!selectedFile) return;

    if (isLockedComunicadosFromInternalArchive(selectedFile)) {
      setDeleteModalOpen(false);
      setSelectedFile(null);
      return;
    }

    const res = await deleteService(selectedFile.id);
    const data = res?.data;

    if (data?.success) {
      await loadServices();
    } else {
      console.error(data);
      alert("Erro ao apagar documento");
    }

    setDeleteModalOpen(false);
    setSelectedFile(null);
  };

  const handleOpenService = async (file) => {
    setOpeningFileId(Number(file.id));

    try {
      const ok = await openFileUrl(
        file.caminho,
        file.nome_original || file.nome_ficheiro,
      );
      if (!ok) window.open(`${FILE_BASE_URL}/${file.caminho}`, "_blank");
    } catch (err) {
      window.open(`${FILE_BASE_URL}/${file.caminho}`, "_blank");
    }

    if (!userId || !userType || !file?.id) {
      setOpeningFileId(0);
      return;
    }

    await markFileAsRead({
      user_id: userId,
      user_type: userType,
      file_type: "service",
      file_id: Number(file.id),
    });

    await refreshUnreadCount();
    setOpeningFileId(0);
  };

  const handleMoveToArchive = async (file) => {
    const fileId = Number(file?.id || 0);
    if (!clientId || !companyId || !fileId) {
      return;
    }

    try {
      setMovingFileId(fileId);
      setArchiveError("");
      setArchiveSuccess("");

      const res = await moveClientFileToClientArchive({
        company_id: companyId,
        client_id: clientId,
        file_type: "servicos",
        file_id: fileId,
      });

      if (!res?.success) {
        throw new Error(
          res?.message || "Não foi possível mover o ficheiro para o arquivo.",
        );
      }

      await loadServices();
      await refreshUnreadCount();
      setArchiveSuccess("Ficheiro movido para o arquivo com sucesso.");
    } catch (error) {
      console.error("Move service to archive error:", error);
      setArchiveError(
        error?.message || "Não foi possível mover o ficheiro para o arquivo.",
      );
    } finally {
      setMovingFileId(0);
    }
  };

  /* ========================= */
  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        Serviços
      </Typography>

      {/* TABS */}
      <Tabs
        value={selectedServiceTab}
        onChange={(_, v) => {
          setSelectedServiceTab(v);
          setSelectedAccountingSubTab(0);
          setSelectedAdministrativoSubTab(0);
          setSelectedFormacaoSubTab(0);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          backgroundColor: "rgba(114, 4, 241, 0.18)",
          borderRadius: 2,
          "& .MuiTabs-indicator": { display: "none" },
        }}
      >
        {serviceCategories.map((cat) => (
          <Tab
            key={cat}
            label={
              getTabUnreadCount(cat) > 0 ? (
                <Badge
                  color="error"
                  badgeContent={getTabUnreadCount(cat)}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  sx={{
                    "& .MuiBadge-badge": {
                      minWidth: 14,
                      height: 14,
                      fontSize: "0.65rem",
                      px: 0.5,
                      top: 2,
                      right: -8,
                    },
                  }}
                >
                  <span>{cat}</span>
                </Badge>
              ) : (
                cat
              )
            }
            sx={{
              textTransform: "none",
              "&.Mui-selected": {
                color: "#fff",
                backgroundColor: "#9c27b0",
              },
            }}
          />
        ))}
      </Tabs>

      {/* SUB-TABS FOR CONTABILIDADE - CUSTOM DESIGN */}
      {selectedServiceTab === 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: "rgba(156, 39, 176, 0.05)",
            borderRadius: 2,
            border: "1px solid rgba(156, 39, 176, 0.2)",
          }}
        >
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1, fontWeight: 600, color: "#666" }}
          >
            Tipo de Documento
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {accountingSubcategories.map((cat, idx) => (
              <Button
                key={cat}
                onClick={() => setSelectedAccountingSubTab(idx)}
                variant={
                  selectedAccountingSubTab === idx ? "contained" : "outlined"
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  transition: "all 0.3s ease",
                  backgroundColor:
                    selectedAccountingSubTab === idx
                      ? "#9c27b0"
                      : "transparent",
                  color: selectedAccountingSubTab === idx ? "#fff" : "#9c27b0",
                  borderColor: "#9c27b0",
                  "&:hover": {
                    backgroundColor:
                      selectedAccountingSubTab === idx
                        ? "#7b1fa2"
                        : "rgba(156, 39, 176, 0.1)",
                    borderColor: "#7b1fa2",
                  },
                }}
              >
                {getSubtabUnreadCount("Contabilidade", cat) > 0 ? (
                  <Badge
                    color="error"
                    badgeContent={getSubtabUnreadCount("Contabilidade", cat)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{
                      "& .MuiBadge-badge": {
                        minWidth: 14,
                        height: 14,
                        fontSize: "0.65rem",
                        px: 0.5,
                        top: 2,
                        right: -8,
                      },
                    }}
                  >
                    <span>{cat}</span>
                  </Badge>
                ) : (
                  cat
                )}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* SUB-TABS FOR ADMINISTRATIVO - CUSTOM DESIGN */}
      {selectedServiceTab === 3 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: "rgba(156, 39, 176, 0.05)",
            borderRadius: 2,
            border: "1px solid rgba(156, 39, 176, 0.2)",
          }}
        >
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1, fontWeight: 600, color: "#666" }}
          >
            Tipo de Documento
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {administrativoSubcategories.map((cat, idx) => (
              <Button
                key={cat}
                onClick={() => setSelectedAdministrativoSubTab(idx)}
                variant={
                  selectedAdministrativoSubTab === idx
                    ? "contained"
                    : "outlined"
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  transition: "all 0.3s ease",
                  backgroundColor:
                    selectedAdministrativoSubTab === idx
                      ? "#9c27b0"
                      : "transparent",
                  color:
                    selectedAdministrativoSubTab === idx ? "#fff" : "#9c27b0",
                  borderColor: "#9c27b0",
                  "&:hover": {
                    backgroundColor:
                      selectedAdministrativoSubTab === idx
                        ? "#7b1fa2"
                        : "rgba(156, 39, 176, 0.1)",
                    borderColor: "#7b1fa2",
                  },
                }}
              >
                {cat}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* SUB-TABS FOR FORMAÇÃO - CUSTOM DESIGN */}
      {selectedServiceTab === 4 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: "rgba(156, 39, 176, 0.05)",
            borderRadius: 2,
            border: "1px solid rgba(156, 39, 176, 0.2)",
          }}
        >
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1, fontWeight: 600, color: "#666" }}
          >
            Tipo de Documento
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {formacaoSubcategories.map((cat, idx) => (
              <Button
                key={cat}
                onClick={() => setSelectedFormacaoSubTab(idx)}
                variant={
                  selectedFormacaoSubTab === idx ? "contained" : "outlined"
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  transition: "all 0.3s ease",
                  backgroundColor:
                    selectedFormacaoSubTab === idx ? "#9c27b0" : "transparent",
                  color: selectedFormacaoSubTab === idx ? "#fff" : "#9c27b0",
                  borderColor: "#9c27b0",
                  "&:hover": {
                    backgroundColor:
                      selectedFormacaoSubTab === idx
                        ? "#7b1fa2"
                        : "rgba(156, 39, 176, 0.1)",
                    borderColor: "#7b1fa2",
                  },
                }}
              >
                {cat}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* UPLOAD BUTTON */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" component="label" color="secondary">
          <UploadFile sx={{ mr: 1 }} />
          Carregar
          <input hidden type="file" onChange={handleServiceUpload} />
        </Button>
      </Box>

      {archiveError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {archiveError}
        </Alert>
      ) : null}

      {archiveSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {archiveSuccess}
        </Alert>
      ) : null}

      {/* LIST */}
      <List dense>
        {/* ================= DB SERVICES ================= */}
        {filteredDbServices.map((file) => (
          <ListItem key={file.id} divider sx={{ display: "block" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Description sx={{ mr: 1, color: "green" }} />

              <ListItemText
                primary={file.nome_original}
                secondary={`${Math.round(file.tamanho / 1024)} KB`}
              />

              <ListItemSecondaryAction>
                <IconButton
                  color="warning"
                  onClick={() => handleOpenService(file)}
                  disabled={openingFileId === Number(file.id)}
                >
                  {openingFileId === Number(file.id) ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Visibility />
                  )}
                </IconButton>
                <IconButton
                  color="secondary"
                  title="Mover para Arquivo"
                  onClick={() => handleMoveToArchive(file)}
                  disabled={movingFileId === Number(file.id)}
                >
                  <Archive />
                </IconButton>
                {!isLockedComunicadosFromInternalArchive(file) ? (
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteService(file)}
                  >
                    <Delete />
                  </IconButton>
                ) : null}
              </ListItemSecondaryAction>
            </Box>

            {/* SHOW DB INFO (READ ONLY) */}
            {file.data_inicio && (
              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  value={file.data_inicio || ""}
                  disabled
                  label="Data"
                />
                <TextField
                  size="small"
                  value={file.observacoes || ""}
                  disabled
                  label="Observações"
                  sx={{ minWidth: 280 }}
                />
              </Box>
            )}
          </ListItem>
        ))}

        {/* ================= LOCAL SERVICES ================= */}
        {localService.map((file, index) => (
          <Formik
            key={`${file.name}-${index}`}
            initialValues={{
              data_inicio: file.data_inicio || "",
              observacoes: file.observacoes || "",
            }}
            validationSchema={getServiceSchema(selectedServiceTab)}
            enableReinitialize
            onSubmit={async (values) => {
              const payload = {
                ...file,
                ...values,
              };

              const uploadedId = await uploadFileToServer(payload);

              if (uploadedId) {
                // ✅ REMOVE LOCAL ENTRY AFTER UPLOAD
                const key =
                  selectedServiceTab === 0
                    ? `${selectedServiceTab}-${selectedAccountingSubTab}`
                    : selectedServiceTab === 3
                      ? `${selectedServiceTab}-${selectedAdministrativoSubTab}`
                      : selectedServiceTab === 4
                        ? `${selectedServiceTab}-${selectedFormacaoSubTab}`
                        : selectedServiceTab;
                setServices((prev) => ({
                  ...prev,
                  [key]: (prev[key] || []).filter((_, i) => i !== index),
                }));

                // ✅ REFRESH DB
                await loadServices();
                await refreshUnreadCount();
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              isValid,
              dirty,
            }) => (
              <ListItem divider sx={{ display: "block" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Description sx={{ mr: 1 }} />

                  <ListItemText
                    primary={file.name}
                    secondary={`${Math.round(file.size / 1024)} KB`}
                  />

                  <ListItemSecondaryAction>
                    <IconButton
                      color="success"
                      onClick={handleSubmit}
                      disabled={!isValid}
                    >
                      <Done />
                    </IconButton>

                    <IconButton
                      color="error"
                      onClick={() => removeService(index)}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </Box>

                {/* SERVICE FIELDS */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mt: 1,
                    flexDirection: "column",
                  }}
                >
                  {errors && typeof errors === "string" && (
                    <Typography variant="caption" color="error" sx={{ mb: 1 }}>
                      {errors}
                    </Typography>
                  )}

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      label="Data"
                      name="data_inicio"
                      type="date"
                      value={values.data_inicio}
                      onChange={handleChange}
                      error={touched.data_inicio && Boolean(errors.data_inicio)}
                      helperText={touched.data_inicio && errors.data_inicio}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>

                  <TextField
                    label="Observações"
                    name="observacoes"
                    multiline
                    rows={2}
                    value={values.observacoes}
                    onChange={handleChange}
                    error={touched.observacoes && Boolean(errors.observacoes)}
                    helperText={touched.observacoes && errors.observacoes}
                  />
                </Box>

                {!dirty && file.uploaded && (
                  <Typography variant="caption" color="success.main">
                    Documento enviado com sucesso
                  </Typography>
                )}
              </ListItem>
            )}
          </Formik>
        ))}
      </List>

      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Eliminar documento</DialogTitle>

        <DialogContent>
          Tens a certeza que queres permanentemente eliminar este documento?
          <br />
          <b>{selectedFile?.nome_original || selectedFile?.name}</b>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>

          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteService}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Servicos;
